/**
 * SuiteAccessLogin — Multi-tenant suite door (PI-3 · Pass 2 P1)
 *
 *   Path:  /suite/:slug/access
 *
 * Loads the public landing config from the backend, lets any registered
 * client log in, then verifies the user owns the slug's trip via the
 * Suite-Door-gated GET /api/client-portal/trips/{tripId}. If they don't,
 * we log them back out and surface a clear "wrong door" message.
 *
 * Replaces the per-tenant JacksonSuiteLogin / TudorSuiteLogin
 * duplication. Those files are kept as thin redirects until external
 * links migrate.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

async function verifyClientOwnsTrip(tripId) {
  try {
    const res = await axios.get(`${API}/api/client-portal/trips/${tripId}`, {
      withCredentials: true,
    });
    return res.status === 200;
  } catch {
    return false;
  }
}

export default function SuiteAccessLogin() {
  const { slug } = useParams();
  const slugLower = (slug || "").toLowerCase();
  const { user, checkSession } = useAuth();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const [landingError, setLandingError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/suite-landing/${slugLower}`)
      .then((res) => { if (alive) setLanding(res.data); })
      .catch(() => alive && setLandingError("This suite door isn't available."));
    return () => { alive = false; };
  }, [slugLower]);

  // If already authed as the right client, drop straight into the suite.
  useEffect(() => {
    if (!user || user.role !== "client" || !landing) return;
    // Use the trip id resolved by the landing endpoint via the access_path
    // (we don't expose the trip_id directly; use the slug instead).
    // Verify ownership before redirecting so we don't loop the user
    // through the wrong tenant.
    let alive = true;
    axios
      .get(`${API}/api/suite-landing/${slugLower}/trip-ref`, { withCredentials: true })
      .then(async (res) => {
        const tid = res.data?.trip_id;
        if (!tid) return;
        const owns = await verifyClientOwnsTrip(tid);
        if (alive && owns) navigate(`/suite/${slugLower}/welcome`, { replace: true });
      })
      .catch(() => { /* stay on the door */ });
    return () => { alive = false; };
  }, [user, landing, slugLower, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const res = await axios.post(
        `${API}/api/auth/login`,
        { email: normalizedEmail, password },
        { withCredentials: true }
      );
      const role = (res?.data?.role || "").toLowerCase();
      if (role !== "client") {
        try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); }
        catch (_e) { /* noop */ }
        setError("These credentials are not for this door. Please use the studio sign-in.");
        setBusy(false);
        return;
      }
      // Resolve the slug → trip_id and verify the user actually owns it.
      const ref = await axios.get(`${API}/api/suite-landing/${slugLower}/trip-ref`, {
        withCredentials: true,
      });
      const tid = ref?.data?.trip_id;
      const owns = tid ? await verifyClientOwnsTrip(tid) : false;
      if (!owns) {
        try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); }
        catch (_e) { /* noop */ }
        setError("Access not found. Please check the email on your invitation.");
        setBusy(false);
        return;
      }
      await checkSession();
      navigate(`/suite/${slugLower}/welcome`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("suite access has ended")) {
        setError("Your suite access has ended. Please contact your advisor.");
      } else {
        setError("Access not found. Please check your email and access code.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (landingError) {
    return (
      <div className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex items-center justify-center px-6">
        <p className="font-accent italic text-[#bdb4a4]">{landingError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex flex-col">
      <header className="px-6 md:px-10 pt-8">
        <p
          className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#cfa75c]"
          data-testid="suite-access-eyebrow"
        >
          {landing?.access?.eyebrow || "VÉRAULT · MEMBER SUITE"}
        </p>
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md md:max-w-lg space-y-6"
          data-testid="suite-access-login-form"
          data-slug={slugLower}
        >
          <div className="space-y-2">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#cfa75c]"
              data-testid="suite-access-family"
            >
              {landing?.access?.family_label || "A Private Suite"}
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl leading-[1.05]"
              data-testid="suite-access-title"
            >
              {landing?.access?.welcome_lines && landing.access.welcome_lines.length > 1 ? (
                <>
                  <span className="block" data-testid="suite-access-title-line-1">
                    {landing.access.welcome_lines[0]}
                  </span>
                  <span className="block" data-testid="suite-access-title-line-2">
                    {landing.access.welcome_lines.slice(1).join(" ")}
                  </span>
                </>
              ) : (
                landing?.access?.welcome_title || "Welcome."
              )}
            </h1>
            {landing?.access?.helper_copy && (
              <p className="font-accent italic text-[#bdb4a4] mt-3 leading-relaxed" data-testid="suite-access-helper">
                {landing.access.helper_copy}
              </p>
            )}
            {!landing && !landingError && (
              <p className="font-accent italic text-[#7d7569] mt-3">Loading your suite…</p>
            )}
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#bdb4a4]">
                {landing?.access?.email_label || "Your email"}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1 w-full bg-transparent border border-[#3a3530] rounded-md px-4 py-3 outline-none focus:border-[#cfa75c]"
                data-testid="suite-access-email"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#bdb4a4]">
                {landing?.access?.code_label || "Access code"}
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full bg-transparent border border-[#3a3530] rounded-md px-4 py-3 outline-none focus:border-[#cfa75c]"
                data-testid="suite-access-password"
              />
            </label>
          </div>

          {error && (
            <p
              className="font-accent italic text-[#e0727a] text-sm"
              data-testid="suite-access-error"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#cfa75c] hover:bg-[#dcb878] disabled:opacity-60 text-black py-3 rounded-md font-mono uppercase tracking-[0.22em] text-[11px]"
            data-testid="suite-access-submit"
          >
            {busy ? "…" : (landing?.access?.button_text || "Access Your Suite")}
          </button>

          <p className="font-accent italic text-[#7d7569] text-xs mt-4" data-testid="suite-access-trouble">
            {landing?.access?.trouble_copy ||
              "Trouble signing in? Reply to your advisor's last note and they'll send a fresh access code."}
          </p>
        </form>
      </main>
      <footer
        className="px-6 py-6 font-mono text-[10px] uppercase tracking-[0.28em] text-[#7d7569]"
        data-testid="suite-access-footer"
      >
        {landing?.access?.footer_text || "Private · Encrypted · Members only"}
      </footer>
    </div>
  );
}
